import { Capacitor } from '@capacitor/core';

const SESSION_KEY = 'ourhertz_session_id';

let currentSessionId: string | null = null;

/** Generate a new session ID and persist to localStorage */
export function generateSessionId(): string {
  currentSessionId = crypto.randomUUID();
  localStorage.setItem(SESSION_KEY, currentSessionId);
  return currentSessionId;
}

/** Get current session ID from memory or localStorage */
export function getCurrentSessionId(): string | null {
  if (!currentSessionId) {
    currentSessionId = localStorage.getItem(SESSION_KEY);
  }
  return currentSessionId;
}

/** Clear session ID on logout */
export function clearSessionId(): void {
  currentSessionId = null;
  localStorage.removeItem(SESSION_KEY);
}

/** Get a human-readable device description */
export function getDeviceInfo(): string {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  return 'Web';
}

/** Collect detailed device info (similar to Expo's lastDeviceInfo) */
export async function getDetailedDeviceInfo(): Promise<Record<string, unknown>> {
  const ua = navigator.userAgent;
  const info: Record<string, unknown> = {
    platform: Capacitor.getPlatform(),
    userAgent: ua,
    language: navigator.language,
    screenWidth: screen.width,
    screenHeight: screen.height,
  };

  // Try Capacitor Device plugin for native details (may not be installed)
  if (Capacitor.isNativePlatform()) {
    try {
      const mod = await import(/* @vite-ignore */ '@capacitor/device');
      const deviceInfo = await mod.Device.getInfo();
      info.manufacturer = deviceInfo.manufacturer;
      info.model = deviceInfo.model;
      info.osName = deviceInfo.operatingSystem;
      info.osVersion = deviceInfo.osVersion;
      info.isVirtual = deviceInfo.isVirtual;
    } catch {}
  }

  // App version from Vite define
  try {
    info.appVersion = __APP_VERSION__;
  } catch {}

  return info;
}

/** Collect network info (similar to Expo's lastNetworkInfo) */
export async function getNetworkInfo(): Promise<Record<string, unknown>> {
  const info: Record<string, unknown> = {
    networkType: (navigator as any).connection?.type ?? (navigator.onLine ? 'online' : 'offline'),
  };

  // Try Capacitor Network plugin
  if (Capacitor.isNativePlatform()) {
    try {
      const mod = await import(/* @vite-ignore */ '@capacitor/network');
      const status = await mod.Network.getStatus();
      info.networkType = status.connectionType; // wifi, cellular, none, unknown
    } catch {}
  }

  return info;
}
